import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { useCallback } from "react";

const COLLABORATION = {
  GET_MY_CHANNELS: gql`
    query GetMyChannels($userId: [ID!]!) {
      GetMyChannels(user_id: $userId) {
        channel_id
        channel_code
        channel_name
        query
        is_owner
        channel_members {
          user_id
          username
          email
          first_name
          last_name
        }
      }
    }
  `,
  GET_CHANNEL_MESSAGES: gql`
    query GetChannelMessages($channelId: ID!) {
      GetChannelMessages(channel_id: $channelId) {
        sender_username
        user_id
        channel_id
        channel_code
        message
        file_path
        time
      }
    }
  `,
  CREATE_CHANNEL: gql`
    mutation CreateChannel($channelName: String!, $users: [ID!]!, $savedQueryId: String!) {
      CreateChannel(channel_name: $channelName, users: $users, saved_query_id: $savedQueryId) {
        channel_id
        channel_code
        channel_name
        saved_query_id
        created_on
        updated_on
      }
    }
  `,
  ADD_MEMBERS_TO_CHANNEL: gql`
    mutation AddMembersToChannel($channelId: String!, $users: [ID!]!) {
      AddMembersToChannel(channel_id: $channelId, users: $users) {
        user_id
        username
        email
        first_name
        last_name
      }
    }
  `,
};

export const useCollaboration = () => {
  const [
    getMyChannelsQuery,
    {
      loading: getMyChannelsLoading,
      error: getMyChannelsError,
      data: getMyChannelsData,
    },
  ] = useLazyQuery(COLLABORATION.GET_MY_CHANNELS, {
    fetchPolicy: "network-only",
  });

  const getMyChannels = useCallback(
    async (userId) => {
      try {
        const { data } = await getMyChannelsQuery({
          variables: {
            userId: [parseInt(userId)],
          },
        });

        if (!data?.GetMyChannels) {
          throw new Error("Failed to fetch channels");
        }

        return {
          success: true,
          data: data.GetMyChannels,
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || "An error occurred while fetching channels",
        };
      }
    },
    [getMyChannelsQuery]
  );

  const [
    getChannelMessagesQuery,
    {
      loading: getChannelMessagesLoading,
      error: getChannelMessagesError,
      data: getChannelMessagesData,
    },
  ] = useLazyQuery(COLLABORATION.GET_CHANNEL_MESSAGES, {
    fetchPolicy: "network-only",
  });

  const getChannelMessages = useCallback(
    async (channelId) => {
      try {
        const { data } = await getChannelMessagesQuery({
          variables: {
            channelId: channelId,
          },
        });

        if (!data?.GetChannelMessages) {
          throw new Error("Failed to fetch channel messages");
        }

        return {
          success: true,
          data: data.GetChannelMessages,
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || "An error occurred while fetching channel messages",
        };
      }
    },
    [getChannelMessagesQuery]
  );

  const [createChannelMutation, { loading: createChannelLoading, error: createChannelError, data: createChannelData }] = useMutation(COLLABORATION.CREATE_CHANNEL);
  
  const [addMembersToChannelMutation, { loading: addMembersLoading, error: addMembersError, data: addMembersData }] = useMutation(COLLABORATION.ADD_MEMBERS_TO_CHANNEL);

  const createChannel = useCallback(
    async (channelName, users, savedQueryId) => {
      try {
        const { data } = await createChannelMutation({
          variables: {
            channelName,
            users,
            savedQueryId,
          },
        });
        if (!data?.CreateChannel) {
          throw new Error("Failed to create channel");
        }
        return {
          success: true,
          data: data.CreateChannel,
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || "An error occurred while creating channel",
        };
      }
    },
    [createChannelMutation]
  );

  const addMembersToChannel = useCallback(
    async (channelId, users) => {
      try {
        const { data } = await addMembersToChannelMutation({
          variables: {
            channelId,
            users,
          },
        });
        if (!data?.AddMembersToChannel) {
          throw new Error("Failed to add members to channel");
        }
        return {
          success: true,
          data: data.AddMembersToChannel,
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || "An error occurred while adding members to channel",
        };
      }
    },
    [addMembersToChannelMutation]
  );

  return {
    getMyChannels,
    getChannelMessages,
    createChannel,
    addMembersToChannel,
    loading: getMyChannelsLoading || getChannelMessagesLoading || createChannelLoading || addMembersLoading,
    error: getMyChannelsError || getChannelMessagesError || createChannelError || addMembersError,
    data: getMyChannelsData || getChannelMessagesData || createChannelData || addMembersData,
  };
};
